// /docs/use-cases — three narrative counter-UAS scenarios.
// Each ≤ 400 words; redacted screenshot; capability-level language only.
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SeoHead from "@/components/SeoHead";

const CASES = [
    {
        id: "urban-canyon-net-capture",
        title: "Urban-canyon net capture",
        image: "/docs/screens/usecase_urban_redacted.svg",
        imageAlt:
            "Redacted Workbench view of an urban-canyon scenario, showing buildings as obstacles and a net-capture engagement attached.",
        body: [
            "A downtown venue has reported a series of rogue multirotor incursions " +
            "between tall buildings. Security engineering needs an engagement " +
            "plan that is both credible and obviously not a kinetic response " +
            "into a populated area.",
            "The operator composes the scenario with a terrain model of the " +
            "venue, traces the surrounding buildings as obstacles, and attaches a " +
            "small multirotor threat on an evasive ingress profile. A net-capture " +
            "engagement is attached — proximity fuze at a short stand-off, single " +
            "shot, modest Pk envelope.",
            "The validation gate immediately surfaces the relevant physical " +
            "constraints: the Pk curve is monotonic, the fuze arms before it " +
            "triggers, and the RF line-of-sight heat-map flags which approach " +
            "corridors have interference from the surrounding structures.",
            "A deterministic Monte Carlo then sweeps the guidance law against " +
            "the threat's jink schedule, showing expected miss distance, detection " +
            "event timing, and — because the engagement is attached — the " +
            "overlaid p(kill) against miss. Reviewer sign-off closes the loop " +
            "before the plan reaches the operator rehearsal.",
        ],
    },
    {
        id: "contested-weather-salvo-engagement",
        title: "Contested-weather salvo engagement",
        image: "/docs/screens/usecase_weather_redacted.svg",
        imageAlt:
            "Redacted Workbench view of a fixed-wing ingress scenario under reduced visibility and a two-shot salvo doctrine.",
        body: [
            "A fixed-wing threat is ingressing under reduced visibility and " +
            "moderate wind. The engagement plan needs to survive weather " +
            "degradation and, if the first shot misses, present a credible " +
            "second-shot window.",
            "The operator assembles a degraded-visibility environment, " +
            "attaches the fixed-wing threat on a dash profile, and picks a " +
            "blast-fragmentation engagement with a two-shot salvo doctrine at a " +
            "fixed spacing. Guidance is proportional navigation with an augmented " +
            "variant alongside for comparison.",
            "The validation gate checks the salvo spacing is physically " +
            "achievable, that the seeker's detection range is appropriate for the " +
            "stated visibility, and that the engagement's Pk curve does not " +
            "pretend to credit miss distances above the warhead's lethal radius.",
            "The run renders both guidance laws in a single comparison chart; " +
            "with the engagement attached, the Pk overlay identifies the better " +
            "law by expected kill probability at the mean miss, and the Best " +
            "Profile card surfaces that verdict for the reviewer.",
        ],
    },
    {
        id: "group-1-swarm-vs-kinetic-interceptor",
        title: "Group-1 swarm vs kinetic interceptor",
        image: "/docs/screens/usecase_swarm_redacted.svg",
        imageAlt:
            "Redacted Workbench view of a group-1 swarm threat with comms-denied fallback waypoints and a kinetic interceptor configuration.",
        body: [
            "A group-1 swarm is approaching a forward operating position; the " +
            "interceptor stockpile is limited, and the defender needs to know " +
            "how many kinetic engagements it can afford before the swarm is " +
            "disrupted.",
            "The operator attaches a swarm threat in comms-denied mode with " +
            "on-detection fallback waypoints, so the threat correctly switches " +
            "behaviour the moment the interceptor's seeker acquires first " +
            "detection. The interceptor carries a contact-fuzed kinetic warhead.",
            "The deterministic Monte Carlo estimates shots-per-kill and the " +
            "probability of total swarm disruption within a stockpile of " +
            "N interceptors. The diagnostic scrubber on each sample shows the " +
            "detection chevron, a lock-loss chevron (if the threat jinks " +
            "hard enough to break track), a re-acquisition chevron, and the " +
            "comms-denied transition marker.",
            "The analyst submits the run; the reviewer verifies the fallback " +
            "waypoints match the threat doctrine in use and approves. The " +
            "approved run is promoted to a golden reference for future trade " +
            "studies against different interceptor inventories.",
        ],
    },
];

export default function DocsUseCasesPage() {
    return (
        <>
            <SeoHead path="/docs/use-cases"
                     title="UAVSimNet Docs — Counter-UAS use cases"
                     description="Three narrative counter-UAS scenarios: urban-canyon net capture, contested-weather salvo engagement, group-1 swarm vs kinetic interceptor." />
            <div data-testid="docs-use-cases-page">
                <section className="section" style={{ paddingTop: 64 }}>
                    <div className="section__inner">
                        <span className="section__eyebrow">// DOCS · 4 OF 4</span>
                        <h1 className="section__heading">Use cases</h1>
                        <p className="section__lede">
                            Three narratives drawn from the capability surface.
                            Each ends with a reviewer sign-off; what a production
                            operator-rehearsal squad would expect to do, end to
                            end.
                        </p>

                        <div style={{ marginTop: 36, display: "grid", gap: 36 }} data-testid="docs-usecase-cards">
                            {CASES.map((c) => (
                                <article
                                    key={c.id}
                                    className="cap-card"
                                    data-testid={`docs-usecase-${c.id}`}
                                    style={{ padding: 20 }}
                                >
                                    <div
                                        style={{
                                            fontFamily:
                                                "'JetBrains Mono', monospace",
                                            color: "var(--ux-accent)",
                                            fontSize: 12,
                                            marginBottom: 6,
                                        }}
                                    >
                                        CASE · {c.id.replace(/-/g, " · ").toUpperCase()}
                                    </div>
                                    <h2
                                        style={{
                                            fontFamily:
                                                "'JetBrains Mono', monospace",
                                            color: "var(--ux-text-1)",
                                            fontSize: 20,
                                            marginBottom: 16,
                                        }}
                                    >
                                        {c.title}
                                    </h2>
                                    <figure style={{ margin: 0 }}>
                                        <img
                                            src={c.image}
                                            alt={c.imageAlt}
                                            style={{
                                                width: "100%",
                                                borderRadius: 6,
                                                display: "block",
                                                border: "1px solid var(--ux-border-1)",
                                            }}
                                        />
                                    </figure>
                                    <div style={{ marginTop: 16 }}>
                                        {c.body.map((para, idx) => (
                                            <p
                                                key={idx}
                                                style={{
                                                    color: "var(--ux-text-2)",
                                                    fontSize: 14,
                                                    lineHeight: 1.65,
                                                    marginTop: idx === 0 ? 0 : 12,
                                                }}
                                            >
                                                {para}
                                            </p>
                                        ))}
                                    </div>
                                </article>
                            ))}
                        </div>

                        <div style={{ marginTop: 48, display: "flex", gap: 14, flexWrap: "wrap" }}>
                            <Link to="/contact" className="btn-primary" data-testid="docs-usecases-contact">
                                Request access <ArrowRight size={14} />
                            </Link>
                            <Link to="/docs" className="btn-ghost" data-testid="docs-usecases-back">
                                Back to docs index
                            </Link>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
}
